import React from 'react'

import { useTheme } from '@emotion/react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ArcadeButton, Stack, Text } from '@cash-design-system/react'
import { InstrumentSelectionBlockerV3 } from '@cash-web/protos/squareup/franklin/blockers/instrument_selection_blocker_v3.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSelectInstrumentV2 } from '@cash-web/shared-data-access-api/react-query/useMutationSelectInstrumentV2'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'

import { BaseBlocker } from '../../BaseBlocker/BaseBlocker'
import { encodeInstrumentSelectionOption } from './encodeInstrumentSelectionOption'
import { InstrumentSelectionV3Option } from './InstrumentSelectionV3Option'

export type BlockerInstrumentSelectionV3Props = {
  blocker: InstrumentSelectionBlockerV3
}

type InstrumentSelectionFormValues = {
  selectedInstrumentToken: string
}

export const BlockerInstrumentSelectionV3: React.FC<BlockerInstrumentSelectionV3Props> = ({ blocker }) => {
  const { spacing } = useTheme()
  const { t } = useTranslation()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const { mutateAsync: selectInstrumentV2 } = useMutationSelectInstrumentV2()
  const isV3Enabled = useFeatureFlag('cash-web-account-enable-instrument-selection-blocker-v3')
  if (!isV3Enabled) {
    throw new Error(
      'InstrumentSelectionBlockerV3 requires cash-web-account-enable-instrument-selection-blocker-v3 feature flag to be enabled'
    )
  }

  const sections = blocker.instrument_sections?.length
    ? blocker.instrument_sections
    : blocker.instrument_options?.length
      ? [{ title: blocker.title, instrument_options: blocker.instrument_options }]
      : []

  const allOptions = sections.flatMap(section => section.instrument_options ?? [])

  const preselectedOption = allOptions.find(option => option.preselected)
  const preselectedOptionToken = preselectedOption?.select_instrument_action?.instrumentToken

  const form = useForm<InstrumentSelectionFormValues>({
    defaultValues: {
      selectedInstrumentToken: preselectedOptionToken ?? '',
    },
  })

  const selectedOptionToken = form.watch('selectedInstrumentToken')

  async function handleSubmit({ selectedInstrumentToken }: InstrumentSelectionFormValues) {
    // Only select_instrument_action options are rendered as radio buttons (link/replace are unsupported),
    // so selectedInstrumentToken will always match a select_instrument_action option.
    const selectedOption = allOptions.find(
      option => option.select_instrument_action?.instrumentToken === selectedInstrumentToken
    )

    if (!selectedOption) {
      throw new Error('InstrumentSelectionV3: could not find selected option')
    }

    const encoded_selected_option = encodeInstrumentSelectionOption(selectedOption)

    await handleBlockerResponse({
      promisedBlockerUpdate: selectInstrumentV2({ encoded_selected_option }),
      defaultError: t('Sorry, an error occurred. Please try again.', {
        context: 'this is the default error message when selecting an instrument errors',
      }),
      // Non-200s throw via rejectOnError — any 200 means the selection was accepted
      isResponseSuccess: () => true,
    })
  }

  return (
    <BaseBlocker
      form={form}
      buttons={[
        <ArcadeButton key="submit" size="cta" prominence="prominent" type="submit" disabled={!selectedOptionToken}>
          {blocker.ctaButtonLabel ?? t('Continue', { context: 'Button label.' })}
        </ArcadeButton>,
      ]}
      onSubmit={handleSubmit}
    >
      <Stack space={spacing.xl}>
        {sections.map(section => (
          <Stack key={section.title} space={spacing.xl}>
            <Text typography="header" color="textStandard" as="h2">
              {section.title}
            </Text>
            <Stack space={spacing.xl}>
              {(section.instrument_options ?? []).map(option => (
                <InstrumentSelectionV3Option
                  key={option.title}
                  {...option}
                  selectedOptionToken={selectedOptionToken}
                  onSelect={optionToken => form.setValue('selectedInstrumentToken', optionToken)}
                />
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </BaseBlocker>
  )
}
